import type React from 'react';
import { useCallback, useState } from 'react';
import classnames from 'classnames';
import { useIntl } from 'react-intl';

import { CollapsibleListSection } from 'ui-components/components/collapsible-list/collapsible-list-section';
import { CollapsibleListSectionContent } from 'ui-components/components/collapsible-list/collapsible-list-section-content';
import { CollapsibleListSectionHeader } from 'ui-components/components/collapsible-list/collapsible-list-section-header';
import { FlexRow } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import UserDateTimezoneFormat from '../../../../../../components/user-date-timezone-format';
import { DetailField } from '../../../components/detail-field/detail-field';
import { messages } from '../../messages';
import styles from './shipping-details.styles.module.less';

type ShippingDetailsProps = {
  orderNumber: string;
  referenceNumber: string | null;
  orderDate: string | null;
  expectedDeliveryDate: string | null;
  notes: string | null;
};

export const ShippingDetails = ({ orderNumber, referenceNumber, orderDate, expectedDeliveryDate, notes }: ShippingDetailsProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const { formatMessage } = useIntl();

  const toggleIsExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const onSectionHeaderClick = useCallback(
    (e: React.MouseEvent) => {
      if (!e?.defaultPrevented) {
        toggleIsExpanded();
      }
    },
    [toggleIsExpanded],
  );

  const headerContent = <Text textType={TextType.H4} value={messages.shippingDetails} />;

  const displayOrderDate = orderDate ? <UserDateTimezoneFormat dateTime={orderDate} /> : null;
  const displayExpectedDeliveryDate = expectedDeliveryDate ? <UserDateTimezoneFormat dateTime={expectedDeliveryDate} /> : null;

  const shippingDetailsContent = (
    <div>
      <FlexRow className={styles['details-grid']}>
        <DetailField className={styles['details-column']} label={messages.orderId} value={orderNumber} />
        <DetailField className={styles['details-column']} label={messages.referenceNumber} value={referenceNumber} />
      </FlexRow>

      <FlexRow className={styles['details-grid']}>
        <DetailField className={styles['details-column']} label={messages.orderDate} value={displayOrderDate} />
        <DetailField className={styles['details-column']} label={messages.expectedDeliveryDate} value={displayExpectedDeliveryDate} />
      </FlexRow>

      <FlexRow className={styles['details-grid']}>
        <DetailField className={styles['full-width-column']} label={messages.notes} value={notes} />
      </FlexRow>
    </div>
  );

  return (
    <section aria-label={formatMessage(messages.shippingDetails)} className={styles['shipping-details-wrapper']}>
      <CollapsibleListSection open={isExpanded}>
        <CollapsibleListSectionHeader
          className={classnames(styles.header, styles['expandable-header-container'])}
          contentClassName={styles['header-content']}
          onClick={onSectionHeaderClick}
          theme='white'
        >
          {headerContent}
        </CollapsibleListSectionHeader>
        <CollapsibleListSectionContent className={styles['expandable-content-container']}>
          {shippingDetailsContent}
        </CollapsibleListSectionContent>
      </CollapsibleListSection>
    </section>
  );
};
